import { useTheme } from 'styled-components';
import { observer } from 'mobx-react';

import type React from 'react';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import type { ValueMeasurementProps } from '@/components/boxes/measurementBoxes/ValueMeasurementBox/ValueMeasurementBox.tsx';

import { getConditionalMeasurementColor } from '@/components/boxes/styles.ts';
import { CriteriaTableCell } from '@/components/boxes/common/Criteria/CriteriaTableCell/CriteriaTableCell.tsx';
import { UpdatableValue } from '@/components/boxes/common/UpdatableValue.tsx';
import { CriteriaTooltip } from '@/components/boxes/common/Criteria/CriteriaTooltip.tsx';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useCriteria } from '../../common/Criteria/useCriteria';

interface Props {
  measurement: ValueMeasurementProps;
  hasCriteria: boolean | undefined;
  index: number;
}

interface ValueMeasurementPropsDetails {
  units: string;
  factor?: number;
}

export const ValueMeasurementRow: React.FC<Props> = observer(({ measurement, hasCriteria, index }) => {
  const theme = useTheme();

  const details = measurement.details as ValueMeasurementPropsDetails | undefined;

  const { criteriaStatus, criteriaMessage } = useCriteria(measurement.descriptor, details?.units, details?.factor);

  return (
    <CriteriaTooltip title={criteriaMessage} key={measurement.descriptor + index}>
      <TableRow
        key={measurement.label}
        data-testid={measurement.descriptor + 'Value'}
        sx={{
          '&:last-child td': { border: 0 }
        }}
      >
        {hasCriteria && <CriteriaTableCell criteriaStatus={criteriaStatus} index={index} />}

        <TableCell
          component="td"
          scope="row"
          key={'label' + index}
          sx={{
            paddingLeft: hasCriteria ? 0 : '1rem',
            color: getConditionalMeasurementColor(hasCriteria, criteriaStatus, theme)
          }}
        >
          {measurement.label}
        </TableCell>

        <TableCell
          component="td"
          scope="row"
          align="right"
          key={'value' + index}
          sx={{
            '&': {
              paddingRight: 1.5,
              color: getConditionalMeasurementColor(hasCriteria, criteriaStatus, theme)
            }
          }}
        >
          <UpdatableValue
            descriptor={measurement.descriptor}
            constructor={DT.ValueMeasurement}
            valueKey="value"
            formatting={(n): string => {
              const value = details?.factor !== undefined ? n * details.factor : n;
              return Number.isInteger(value) ? value.toFixed() : value.toFixed(1);
            }}
            isUndefinedOk={true}
          />{' '}
          {details?.units ?? ''}
        </TableCell>
      </TableRow>
    </CriteriaTooltip>
  );
});
